import {
  Analytics,
  type Partner,
  type Product
} from '@mzc-watchover/adminapp-appsync'
import { find, isEmpty, isNil, map } from 'lodash/fp'
import log from 'loglevel'
import React from 'react'
import {
  CheckboxGroupInput,
  Confirm,
  Form,
  Loading,
  RadioButtonGroupInput,
  useGetList,
  useTranslate
} from 'react-admin'
import { useFormContext } from 'react-hook-form'
import { Button, Grid, Typography } from '@mui/material'

log.setLevel(log.levels.DEBUG)

const productChoices = [
  { id: Analytics.activity, name: 'テラシテR' },
  { id: 'solar', name: 'テラシテS' },
  { id: Analytics.disasterInfoOnly, name: '安否共有' }
]

const PartnerSelectContent: React.FC<{
  setPartners: (partners: Array<Partial<Partner>>) => void
  partners: Array<Partial<Partner>> | undefined
}> = ({ setPartners, partners }) => {
  const translate = useTranslate()
  const [page, setPage] = React.useState<number>(1)
  const [choices, setChoices] = React.useState<string[]>([
    Analytics.activity,
    Analytics.disasterInfoOnly,
    'solar'
  ])
  const { getValues } = useFormContext()
  const { data, isLoading, total } = useGetList<
    { id: string } & Partial<Partner>
  >('partners', {
    pagination: { page, perPage: 10 },
    filter: {
      products: map((item: string) => ({ key: 'analytics', value: item }))(
        choices
      )
    }
  })
  const onPrev = (): void => {
    setPage(page - 1)
  }
  const onNext = (): void => {
    setPage(page + 1)
  }
  const onChoice = (): void => {
    const { products } = getValues()
    setChoices(products)
    setPage(1)
  }
  React.useEffect(() => {
    if (isLoading) return
    // const partners = map((item: Partial<Partner>) => {
    //   const { id, name, products } = item
    //   return {
    //     id: id as string,
    //     name: name as string,
    //     products: products as Product[]
    //   }
    // })(data)
    setPartners(data as Array<Partial<Partner>>)
  }, [isLoading, data])
  return (
    <Grid
      container
      sx={{
        display: 'flex',
        justifyContent: 'center'
      }}
      spacing={2}>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <Typography variant="h6">
          {translate('resources.partnerSelect.dialog.title')}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        sx={{
          display: 'flex',
          justifyContent: 'center'
        }}>
        <CheckboxGroupInput
          source="products"
          choices={productChoices}
          label={false}
          defaultValue={[
            Analytics.activity,
            Analytics.disasterInfoOnly,
            'solar'
          ]}
          onChange={onChoice}
        />
      </Grid>
      <Grid item xs={12}>
        {isLoading || isNil(partners) ? (
          !isEmpty(choices) ? (
            <Loading />
          ) : (
            <></>
          )
        ) : (
          <RadioButtonGroupInput
            source="partner"
            choices={partners}
            row={false}
            label={false}
          />
        )}
      </Grid>
      <Grid item>
        <Button variant="contained" onClick={onPrev} disabled={page === 1}>
          {translate('resources.partnerSelect.dialog.buttons.prev')}
        </Button>
      </Grid>
      <Grid item>
        <Button
          variant="contained"
          onClick={onNext}
          disabled={isNil(total) || page >= total / 10}>
          {translate('resources.partnerSelect.dialog.buttons.next')}
        </Button>
      </Grid>
    </Grid>
  )
}

const PartnerSelectDialogContent: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  setProducts: (products: Product[]) => void
  setPartnerName: (name: string) => void
  setPartner: ((partner: Partial<Partner>) => void) | undefined
}> = ({ open, setOpen, setProducts, setPartnerName, setPartner }) => {
  const [partners, setPartners] = React.useState<
    Array<Partial<Partner>> | undefined
  >(undefined)
  const { getValues } = useFormContext()
  const onClose = (): void => {
    setOpen(false)
  }
  const onConfirm = (): void => {
    const values = getValues()
    const { partner } = values
    if (!isNil(partner)) {
      const item = find((item: Partial<Partner>) => item.id === partner)(
        partners
      )
      if (!isNil(item)) {
        const { name, products } = item
        if (!isNil(setPartner)) setPartner(item)
        setPartnerName(`${name as string}さま`)
        setProducts(products as Product[])
        setOpen(false)
      }
    }
  }
  return (
    <Confirm
      isOpen={open}
      title={false}
      content={
        <PartnerSelectContent setPartners={setPartners} partners={partners} />
      }
      onClose={onClose}
      onConfirm={onConfirm}
      cancel="resources.partnerSelect.dialog.buttons.cancel"
      confirm="resources.partnerSelect.dialog.buttons.confirm"
    />
  )
}

export const PartnerSelectDialog: React.FC<{
  open: boolean
  setOpen: (open: boolean) => void
  setProducts: (products: Product[]) => void
  setPartnerName: (name: string) => void
  setPartner: ((partner: Partial<Partner>) => void) | undefined
}> = ({ open, setOpen, setProducts, setPartnerName, setPartner }) => {
  return (
    <Form
      defaultValues={{
        products: [Analytics.activity, Analytics.disasterInfoOnly, 'solar']
      }}>
      <PartnerSelectDialogContent
        open={open}
        setOpen={setOpen}
        setProducts={setProducts}
        setPartnerName={setPartnerName}
        setPartner={setPartner}
      />
    </Form>
  )
}
